import XLSX from "xlsx-js-style";
import moment from "moment";

const PINK_FILL = "FCE4EC";
const YELLOW_FILL = "FFF9C4";
const HEADER_FILL = "EAEAEA";

const monthLabel = (iso) => moment(iso, "YYYY-MM-DD").format("MMM-YYYY");

const expandMonthColumns = (start, end) => {
  const cols = [];
  let cur = moment(start, "YYYY-MM-DD").startOf("month");
  const last = moment(end, "YYYY-MM-DD").startOf("month");
  while (cur.isSameOrBefore(last, "month")) {
    cols.push(cur.format("YYYY-MM-DD"));
    cur = cur.add(1, "month");
  }
  return cols;
};

const pivotRows = (rows) => {
  const map = new Map();
  rows.forEach((r) => {
    const key = [
      r.cmoOrgID,
      r.sponsorOrgID,
      r.collabrixProductID,
      r.productCode,
      r.productDescription,
      r.uom,
    ].join("||");
    if (!map.has(key)) {
      map.set(key, {
        cmoOrgID: r.cmoOrgID,
        sponsorOrgID: r.sponsorOrgID,
        productCode: r.productCode,
        productDescription: r.productDescription,
        uom: r.uom,
        months: {},
      });
    }
    map.get(key).months[r.forecastMonth] = {
      quantity: r.quantity,
      exceedsLowLimit: r.exceedsLowLimit,
      exceedsHighLimit: r.exceedsHighLimit,
    };
  });
  return Array.from(map.values());
};

export function buildMonthlyReportSheetName(monthRange) {
  const start = moment(monthRange.start, "YYYY-MM-DD");
  return `Monthly Report of ${start.format("MMM-YYYY")}`;
}

const headerStyle = {
  font: { bold: true },
  fill: { patternType: "solid", fgColor: { rgb: HEADER_FILL } },
  alignment: { horizontal: "center", vertical: "center" },
  border: {
    top: { style: "thin", color: { rgb: "CCCCCC" } },
    bottom: { style: "thin", color: { rgb: "CCCCCC" } },
    left: { style: "thin", color: { rgb: "CCCCCC" } },
    right: { style: "thin", color: { rgb: "CCCCCC" } },
  },
};

const cellBorder = {
  top: { style: "thin", color: { rgb: "EEEEEE" } },
  bottom: { style: "thin", color: { rgb: "EEEEEE" } },
  left: { style: "thin", color: { rgb: "EEEEEE" } },
  right: { style: "thin", color: { rgb: "EEEEEE" } },
};

const buildMonthlySheet = ({ rows, cmoLabel, brand, monthRange, columnHeader = "CMO" }) => {
  const months = expandMonthColumns(monthRange.start, monthRange.end);
  const pivoted = pivotRows(rows);

  const header = [
    columnHeader,
    "Brand",
    "Material Code",
    "Material Description",
    "UoM",
    ...months.map(monthLabel),
  ];

  const aoa = [header];
  pivoted.forEach((p) => {
    const row = [
      cmoLabel,
      brand,
      p.productCode,
      p.productDescription,
      p.uom,
      ...months.map((m) => (p.months[m] ? p.months[m].quantity : "")),
    ];
    aoa.push(row);
  });

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // header styles
  for (let c = 0; c < header.length; c += 1) {
    const ref = XLSX.utils.encode_cell({ r: 0, c });
    if (ws[ref]) ws[ref].s = headerStyle;
  }

  const baseColCount = 5;
  // body styles
  pivoted.forEach((p, idx) => {
    const r = idx + 1;
    for (let c = 0; c < header.length; c += 1) {
      const ref = XLSX.utils.encode_cell({ r, c });
      if (!ws[ref]) continue;
      const baseCellStyle = { border: cellBorder };
      if (c >= baseColCount) {
        baseCellStyle.alignment = { horizontal: "right" };
      }
      ws[ref].s = baseCellStyle;
    }
    months.forEach((m, mi) => {
      const cell = p.months[m];
      if (!cell) return;
      let fill = null;
      if (cell.exceedsLowLimit) fill = PINK_FILL;
      else if (cell.exceedsHighLimit) fill = YELLOW_FILL;
      if (!fill) return;
      const ref = XLSX.utils.encode_cell({ r, c: baseColCount + mi });
      if (ws[ref]) {
        ws[ref].s = {
          ...(ws[ref].s || {}),
          fill: { patternType: "solid", fgColor: { rgb: fill } },
        };
      }
    });
  });

  ws["!cols"] = [
    { wch: 18 },
    { wch: 18 },
    { wch: 18 },
    { wch: 36 },
    { wch: 8 },
    ...months.map(() => ({ wch: 12 })),
  ];

  return ws;
};

const buildLegendSheet = () => {
  const aoa = [
    ["Legend", "Description"],
    ["", "Outside Low Limit"],
    ["", "Outside High Limit"],
  ];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!cols"] = [{ wch: 14 }, { wch: 36 }];
  ["A1", "B1"].forEach((ref) => {
    if (ws[ref]) ws[ref].s = headerStyle;
  });
  if (ws["A2"]) {
    ws["A2"].s = {
      fill: { patternType: "solid", fgColor: { rgb: PINK_FILL } },
      border: cellBorder,
    };
  }
  if (ws["A3"]) {
    ws["A3"].s = {
      fill: { patternType: "solid", fgColor: { rgb: YELLOW_FILL } },
      border: cellBorder,
    };
  }
  if (ws["B2"]) ws["B2"].s = { border: cellBorder };
  if (ws["B3"]) ws["B3"].s = { border: cellBorder };
  return ws;
};

export function buildForecastSheets({ rows, cmoLabel, brand, monthRange, columnHeader = "CMO" }) {
  return {
    monthly: buildMonthlySheet({ rows, cmoLabel, brand, monthRange, columnHeader }),
    legend: buildLegendSheet(),
    monthlyName: buildMonthlyReportSheetName(monthRange),
    legendName: "Legend",
  };
}

export function buildForecastWorkbook({ rows, cmoLabel, brand, monthRange, columnHeader = "CMO" }) {
  const { monthly, legend, monthlyName, legendName } = buildForecastSheets({
    rows,
    cmoLabel,
    brand,
    monthRange,
    columnHeader,
  });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, monthly, monthlyName);
  XLSX.utils.book_append_sheet(wb, legend, legendName);
  const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  return new Blob([wbout], {
    type:
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

const escapeHtml = (v) =>
  String(v == null ? "" : v)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const styleToCss = (s, colWidth) => {
  if (!s) return colWidth ? `width:${colWidth}px;` : "";
  const css = [];
  if (s.fill && s.fill.fgColor && s.fill.fgColor.rgb) {
    css.push(`background-color:#${s.fill.fgColor.rgb};`);
  }
  if (s.font) {
    if (s.font.bold) css.push("font-weight:600;");
    if (s.font.color && s.font.color.rgb) {
      css.push(`color:#${s.font.color.rgb};`);
    }
  }
  if (s.alignment && s.alignment.horizontal) {
    css.push(`text-align:${s.alignment.horizontal};`);
  }
  if (colWidth) css.push(`min-width:${colWidth}px;`);
  return css.join("");
};

export function sheetToStyledHtml(ws) {
  const ref = ws["!ref"];
  if (!ref) return "<table></table>";
  const range = XLSX.utils.decode_range(ref);
  const cols = ws["!cols"] || [];
  const styleBase =
    "border-collapse:collapse;font-family:Calibri,Arial,sans-serif;font-size:13px;width:100%;";
  const cellBase =
    "border:1px solid #d0d7de;padding:6px 10px;white-space:nowrap;";

  let html = `<table style="${styleBase}"><tbody>`;
  for (let r = range.s.r; r <= range.e.r; r += 1) {
    html += "<tr>";
    for (let c = range.s.c; c <= range.e.c; c += 1) {
      const addr = XLSX.utils.encode_cell({ r, c });
      const cell = ws[addr];
      const colW = cols[c] && cols[c].wch ? cols[c].wch * 8 : null;
      const css = cellBase + styleToCss(cell && cell.s, colW);
      const tag = r === 0 ? "th" : "td";
      const val = cell ? (cell.w != null ? cell.w : cell.v) : "";
      html += `<${tag} style="${css}">${escapeHtml(val)}</${tag}>`;
    }
    html += "</tr>";
  }
  html += "</tbody></table>";
  return html;
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function buildForecastFilename({ cmoLabel, brand, syncId }) {
  const safe = (s) => String(s || "").replace(/\s+/g, "");
  if (!syncId || syncId === 0) {
    const today = moment().format("DDMMMYYYY");
    return `Forecast_${safe(cmoLabel)}_${safe(brand)}_${today}.xlsx`;
  }
  return `ForecastHistory_${safe(cmoLabel)}_${safe(brand)}_${syncId}.xlsx`;
}
